import React from "react";
import { Link } from "react-router-dom";

const Section = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-2xl lg:text-3xl">
      <span className="text-accent-foreground bg-accent px-2 py-0.5 mr-3 text-lg font-mono align-middle">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-primary/90">
      {children}
    </div>
  </section>
);

export default function Terms() {
  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[900px] px-5 lg:px-8 py-14">
          <div className="label-caps text-accent mb-3">Legal</div>
          <h1 className="font-display text-4xl lg:text-6xl leading-[1.05]">Terms of Service</h1>
          <p className="mt-6 text-primary-foreground/80">Veterans Disability Education</p>
          <p className="mt-1 text-sm text-primary-foreground/60 font-mono">Effective date: August 1, 2026</p>
        </div>
      </section>

      <article className="mx-auto max-w-[900px] px-5 lg:px-8 py-14">
        <p className="text-lg leading-relaxed text-primary/90">
          Welcome to Veterans Disability Education ("VDE," "we," "us"). These Terms of Service ("Terms") govern your use of veteransdisabilityeducation.com, our veteran portal, our calculators and tools, and our education and coaching services (together, the "Services"). By using the Services, you agree to these Terms. If you do not agree, please do not use the Services.
        </p>

        <Section n={1} title="What VDE Is and Is Not">
          <p>VDE provides education, coaching, and strategic guidance to help veterans understand the VA disability claims process.</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>VDE and its founder are not VA-accredited claims agents, attorneys, or Veterans Service Organization representatives, and do not prepare, present, or prosecute claims for VA benefits on your behalf.</li>
            <li>Nothing in the Services is legal advice, medical advice, or a substitute for either. You remain fully responsible for your own claim, its contents, and its filing.</li>
            <li>VDE is not affiliated with, endorsed by, or connected to the U.S. Department of Veterans Affairs or any government agency.</li>
            <li>Free assistance with preparing and filing VA claims is available from VA-accredited Veterans Service Organizations. A directory is available at <a href="https://www.va.gov" target="_blank" rel="noreferrer" className="underline underline-offset-4">va.gov</a>.</li>
            <li>Every claim is different. No outcome, rating, or benefit amount is promised or guaranteed. Past results described on the Site do not predict your result.</li>
          </ul>
        </Section>

        <Section n={2} title="Eligibility and Accounts">
          <p>You must be at least 18 years old to use the Services. When you create a portal account you agree to provide accurate information, keep your login credentials confidential, and notify us promptly of any unauthorized use. You are responsible for activity under your account. We may suspend or close accounts that violate these Terms.</p>
        </Section>

        <Section n={3} title="Calculators and Tools">
          <p>Our calculators produce estimates for education only, based on published VA compensation rates and rating rules as we understand them at the time of update. Your actual rating and payment are determined solely by the VA. Rates change periodically and the Site may lag official updates. Do not make financial decisions based on calculator output.</p>
        </Section>

        <Section n={4} title="Your Documents">
          <p>You may upload documents to your portal to support the education and strategy guidance you receive. You represent that you have the right to share what you upload. You keep ownership of your documents. You grant VDE permission to view, store, and organize them solely to provide the Services. Our handling of your documents is described in our <Link to="/privacy" className="underline underline-offset-4">Privacy Policy</Link>. Do not email documents to us; the portal is the only accepted channel.</p>
        </Section>

        <Section n={5} title="Payments">
          <p>Coaching packages are not sold on this website. If, after your free 20-minute consult, we agree that a paid coaching engagement is a good fit, VDE will send you an invoice separately through Stripe. All amounts are in U.S. dollars.</p>
          <p><strong>All sales are final. No refunds, cancellations, or exchanges are offered under any circumstances</strong>, including but not limited to: sessions you do not attend, sessions you attend and later feel differently about, changes in your VA claim strategy or outcome, or personal circumstances that change after purchase.</p>
          <p>VDE does not charge fees contingent on the outcome of any VA claim, and no payment to VDE is for the preparation or filing of a claim.</p>
        </Section>

        <Section n={6} title="Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Use the Services for any unlawful purpose or to submit false information</li>
            <li>Upload malicious code or attempt to access accounts or data that are not yours</li>
            <li>Scrape, copy, resell, or republish Site content or tools without written permission</li>
            <li>Harass or abuse VDE personnel or other users</li>
          </ul>
        </Section>

        <Section n={7} title="Intellectual Property">
          <p>The Site, its content, tools, guides, and materials are owned by VDE or its licensors and are protected by law. We grant you a personal, non-transferable license to use them for your own education. Purchasing a package does not transfer ownership of any materials.</p>
        </Section>

        <Section n={8} title="Third-Party Services">
          <p>The Services rely on third parties such as Calendly (for scheduling), Google (for authentication), and Stripe (for invoicing after a signed engagement, outside this website). Your use of those services is governed by their own terms. VDE is not responsible for third-party services.</p>
        </Section>

        <Section n={9} title="Disclaimers">
          <p>The Services are provided "as is" and "as available" without warranties of any kind, express or implied, including fitness for a particular purpose and accuracy of information. VDE does not warrant that the Services will be uninterrupted or error-free.</p>
        </Section>

        <Section n={10} title="Limitation of Liability">
          <p>To the fullest extent permitted by law, VDE and its founder will not be liable for indirect, incidental, consequential, special, or punitive damages, or for lost benefits, lost claims, missed deadlines, or decisions made by the VA. Our total liability for any claim relating to the Services will not exceed the amount you paid to VDE in the twelve months before the claim arose, or one hundred dollars if you paid nothing. Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.</p>
        </Section>

        <Section n={11} title="Your Responsibility for Deadlines">
          <p>VA claims involve strict deadlines. VDE does not track, calendar, or accept responsibility for any filing deadline, appeal window, or response date connected to your claim. Meeting deadlines is solely your responsibility.</p>
        </Section>

        <Section n={12} title="Indemnification">
          <p>You agree to defend and hold harmless VDE and its founder from claims and expenses arising from your violation of these Terms, your misuse of the Services, or information you submit.</p>
        </Section>

        <Section n={13} title="Termination">
          <p>You may close your account at any time. We may suspend or terminate access for violations of these Terms. Sections that by their nature should survive termination (including 7, 9, 10, and 12) survive.</p>
        </Section>

        <Section n={14} title="Governing Law and Disputes">
          <p>These Terms are governed by the laws of the State of Georgia, without regard to conflict of law rules. Any dispute will be resolved in the state or federal courts located in Georgia, and you consent to their jurisdiction. [Optional: consider an arbitration clause; discuss with your attorney.]</p>
        </Section>

        <Section n={15} title="Changes to These Terms">
          <p>We may update these Terms. Material changes will be posted on the Site with an updated effective date. Continued use after changes take effect means you accept the updated Terms.</p>
        </Section>

        <Section n={16} title="Contact">
          <div className="bg-secondary p-6 border-l-4 border-accent">
            <div className="font-display text-lg mb-2">Veterans Disability Education</div>
            <div className="text-primary/85">Email: <a href="mailto:intake@veteransdisabilityeducation.com" className="underline underline-offset-4">intake@veteransdisabilityeducation.com</a></div>
            <div className="text-primary/85 mt-1">684 John Wesley Dobbs Ave NE Unit E<br />Atlanta, GA 30312</div>
          </div>
        </Section>

        <div className="mt-14 pt-8 border-t border-border flex items-center justify-between text-sm">
          <Link to="/privacy" className="text-primary hover:underline underline-offset-4">Privacy Policy →</Link>
          <Link to="/" className="text-primary hover:underline underline-offset-4">Back to home</Link>
        </div>
      </article>
    </div>
  );
}
